import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { getLogger } from '@confluence/logger';

import type {
	DwellTimeHelper,
	DwellBaseData,
	DwellEventForContent,
	DwellEventForSpace,
	DwellCallback,
	DwellCallbackData,
	Content,
} from './DwellTimeHelper.ts';

export type DwellTimeHelperProvider = (
	sendDwellEvent: DwellCallback,
	dwellData: DwellBaseData,
	searchSessionId?: string,
) => DwellTimeHelper;

const logger = getLogger('DwellTimeTracker');

export class DwellTimeTracker {
	private provideDwellTimeHelper: DwellTimeHelperProvider;
	private dwellTimeHelper: DwellTimeHelper | null = null;
	private dwellTimeCallback: DwellCallback | null = null;

	constructor(provideDwellTimeHelper: DwellTimeHelperProvider) {
		this.provideDwellTimeHelper = provideDwellTimeHelper;
	}

	/**
	 *  Stops previous dwell timer if any and starts a new one.
	 *  `callback` is called at every dwell milestones and when the timer is stopped
	 **/

	async startAny(data: DwellBaseData, callback: DwellCallback): Promise<void> {
		void this.startDwellTimeHelper(data, callback);
	}

	/**
	 *  Same as `startAny()`, but for Confluence Page or Blog and other content types
	 **/

	async startContent(
		spaceId: string,
		contentId: string,
		contentType: Content,
		callback: DwellCallback,
		searchSessionId?: string,
		additionalAnalytics?: object,
	): Promise<void> {
		const dwellData: DwellEventForContent = this.getEventDataForContent(
			spaceId,
			contentId,
			contentType,
			additionalAnalytics,
		);
		await this.startDwellTimeHelper(dwellData, callback, searchSessionId).catch((e) => {
			throw e;
		});
	}

	/**
	 *  Same as `startAny()`, but for Confluence Space Overview
	 **/

	async startSpace(
		spaceId: string,
		callback: DwellCallback,
		searchSessionId?: string,
		additionalAnalytics?: object,
	): Promise<void> {
		const dwellData: DwellEventForSpace = this.getEventDataForSpace(spaceId, additionalAnalytics);
		await this.startDwellTimeHelper(dwellData, callback, searchSessionId).catch((e) => {
			throw e;
		});
	}

	/**
	 *  Stops current timer and reports final dwell time
	 **/

	stop(): void {
		if (this.dwellTimeHelper) {
			this.dwellTimeHelper.stop();
		} else {
			logger.error`Dwell was stopped before it can get started`;
		}
	}

	private async startDwellTimeHelper(
		dwellData: DwellBaseData,
		sendDwellEvent: DwellCallback,
		searchSessionId?: string,
	) {
		try {
			if (!this.dwellTimeHelper) {
				this.dwellTimeHelper = this.provideDwellTimeHelper(
					this.onDwell,
					dwellData,
					searchSessionId,
				);
				// We call stop just to make sure TimeMe.active is false and prevent the following error
				// "DwellTimeHelper should only be initialised once per page load.""
				// https://bitbucket.org/atlassian/analytics-web-client/src/aa020a05be67317714977ceed0221e5721f1a4eb/src/dwellTimeHelper.js#lines-78
				this.dwellTimeHelper.stop();
				this.dwellTimeHelper.start();
			} else {
				this.dwellTimeHelper.newPage(dwellData, searchSessionId);
			}
			this.dwellTimeCallback = sendDwellEvent;
		} catch (e) {
			logger.error`Failed to start dwell time`;
			getMonitoringClient().submitError(e, { attribution: 'disco' });
			throw e;
		}
	}

	private onDwell = (data: DwellCallbackData) => {
		if (this.dwellTimeCallback) {
			this.dwellTimeCallback(data);
		}
	};

	private getEventDataForContent = (
		spaceId: string,
		contentId: string,
		contentType: Content,
		additionalAnalytics: object = {},
	): DwellEventForContent => {
		const analyticsContentType = contentType === 'blogpost' ? 'page' : contentType;
		return {
			containerType: 'space',
			containerId: spaceId,
			objectType: analyticsContentType,
			objectId: contentId,
			source: analyticsContentType,
			actionSubject: analyticsContentType,
			actionSubjectId: contentId,
			attributes: {
				...additionalAnalytics,
			},
		};
	};

	private getEventDataForSpace = (
		spaceId: string,
		additionalAnalytics: object = {},
	): DwellEventForSpace => ({
		containerType: 'space',
		containerId: spaceId,
		source: 'space',
		actionSubject: 'space',
		actionSubjectId: spaceId,
		attributes: {
			...additionalAnalytics,
		},
	});
}
